import { useEffect, useLayoutEffect, useRef, useState } from "react";
import cn from "mcn";
import type { MenuConfig } from "../menu-system/types";
import type { ActionContext } from "../actions/types";
import { useNewFeatureTracking } from "../menu-system/hooks/useNewFeatureTracking";
import { ChevronRightIcon } from "../icons/ChevronRightIcon";
import { useThemeMode } from "../../context/theme";
import { CheckIcon } from "../icons/CheckIcon";
import { isMobile } from "../../shared/hooks";
import { Tooltip, TooltipTrigger } from "@artis/ui";

const SUBMENU_DELAY = 300;
/** Matches the tooltip panel's `max-w-[300px]`; used to pick a side that fits. */
const TOOLTIP_MAX_WIDTH = 300;

export type ContextMenuProps<Ctx = ActionContext> = {
  /** Menu items to display */
  items: MenuConfig<Ctx>[];
  /** Menu context for resolvers */
  context: Ctx;
  /** Position where menu should appear. For submenus, pass parentRect for proper left/right positioning. */
  position: { x: number; y: number; parentRect?: DOMRect };
  /** Callback when menu closes */
  onClose: () => void;
};

export const ContextMenu = <Ctx = ActionContext>({ items, context, position, onClose }: ContextMenuProps<Ctx>) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const openTimeoutRef = useRef<number | null>(null);
  const [submenuState, setSubmenuState] = useState<{
    itemId: string;
    position: { x: number; y: number; parentRect?: DOMRect };
    key: number;
  } | null>(null);
  const submenuKeyRef = useRef(0);
  const [adjustedPosition, setAdjustedPosition] = useState<{
    x: number;
    y: number;
    maxHeight?: number;
    tooltipPlacement: "left" | "right";
    isReady: boolean;
  } | null>(null);
  const [clickedNewItems, setClickedNewItems] = useState<Set<string>>(new Set());
  const { isNew, markAsUsed } = useNewFeatureTracking();
  const isDark = useThemeMode() === "dark";

  const hasAnyIcon = items.some((item) => item !== "separator" && (item.icon || item.isNew));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const isInsideAnyMenu = target.closest("[data-context-menu]");
      if (!isInsideAnyMenu) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!menuRef.current) return;

    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportPadding = 10;
    const gap = 2;

    let adjustedX: number;
    const minX = viewportPadding;
    const maxX = Math.max(viewportPadding, window.innerWidth - menuRect.width - viewportPadding);

    if (position.parentRect) {
      const parentRect = position.parentRect;
      const openRightX = parentRect.right + gap;
      const openLeftX = parentRect.left - menuRect.width - gap;

      if (openRightX <= maxX) {
        adjustedX = openRightX;
      } else if (openLeftX >= minX) {
        adjustedX = openLeftX;
      } else {
        // If the submenu cannot fully fit on either side, anchor to the furthest
        // visible right position to maximize usable width on small screens.
        adjustedX = maxX;
      }
    } else {
      adjustedX = position.x;
      if (position.x + menuRect.width > window.innerWidth - viewportPadding) {
        adjustedX = maxX;
      }
      if (adjustedX < minX) {
        adjustedX = minX;
      }
    }

    let adjustedY = position.y;
    let constrainedHeight: number | undefined;

    if (adjustedY < viewportPadding) {
      adjustedY = viewportPadding;
    }

    const maxBottom = window.innerHeight - viewportPadding;
    const menuBottom = adjustedY + menuRect.height;

    if (menuBottom > maxBottom) {
      const idealTop = maxBottom - menuRect.height;
      if (idealTop >= viewportPadding) {
        adjustedY = idealTop;
      } else {
        adjustedY = viewportPadding;
        constrainedHeight = maxBottom - viewportPadding - 10;
      }
    }

    const roomOnRight = window.innerWidth - (adjustedX + menuRect.width);

    setAdjustedPosition({
      x: adjustedX,
      y: adjustedY,
      maxHeight: constrainedHeight,
      tooltipPlacement: roomOnRight >= TOOLTIP_MAX_WIDTH + viewportPadding ? "right" : "left",
      isReady: true,
    });
  }, [position]);

  const handleItemClick = (item: MenuConfig<Ctx>) => {
    if (item === "separator") return;

    if (item.isNew) {
      markAsUsed(item.id);
      setClickedNewItems((prev) => new Set(prev).add(item.id));
    }

    const action = typeof item.action === "function" ? item.action : null;
    if (action) {
      const result = action(context);
      if (typeof result === "function") {
        result();
      }
    }

    const hasSubMenu = (getSubMenu(item)?.length ?? 0) > 0;
    if (!hasSubMenu) {
      onClose();
    }
  };

  const handleSubmenuEnter = (item: MenuConfig<Ctx>, event: React.MouseEvent<HTMLButtonElement>) => {
    if (item === "separator") return;

    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    const hasSubMenu = (getSubMenu(item)?.length ?? 0) > 0;
    const disabled = isDisabled(item);

    if (hasSubMenu && !disabled) {
      if (submenuState?.itemId === item.id) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      openTimeoutRef.current = window.setTimeout(() => {
        openTimeoutRef.current = null;
        submenuKeyRef.current += 1;
        setSubmenuState({
          itemId: item.id,
          position: {
            x: rect.right + 2,
            y: rect.top - 7,
            parentRect: rect,
          },
          key: submenuKeyRef.current,
        });
      }, SUBMENU_DELAY);
    } else {
      // Item has no submenu - delay before closing current submenu
      closeTimeoutRef.current = window.setTimeout(() => {
        closeTimeoutRef.current = null;
        setSubmenuState(null);
      }, SUBMENU_DELAY);
    }
  };

  const handleSubmenuLeave = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    if (submenuState === null) return;

    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setSubmenuState(null);
    }, SUBMENU_DELAY);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
      if (openTimeoutRef.current !== null) {
        window.clearTimeout(openTimeoutRef.current);
      }
    };
  }, []);

  const isDisabled = (item: MenuConfig<Ctx>): boolean => {
    if (item === "separator") return false;

    if (typeof item.disabled === "boolean") {
      return item.disabled;
    }
    if (typeof item.disabled === "function") {
      return item.disabled(context);
    }
    return false;
  };

  const getTooltip = (item: MenuConfig<Ctx>): string | undefined => {
    if (item === "separator") return undefined;

    if (typeof item.tooltip === "string") {
      return item.tooltip;
    }
    if (typeof item.tooltip === "function") {
      return item.tooltip(context);
    }
    return undefined;
  };

  const getLabel = (item: MenuConfig<Ctx>): string => {
    if (item === "separator") return "";

    if (typeof item.label === "string") {
      return item.label;
    }
    if (typeof item.label === "function") {
      return item.label(context);
    }
    return "";
  };

  const getIcon = (item: MenuConfig<Ctx>): React.ReactNode => {
    if (item === "separator") return null;

    if (typeof item.icon === "function") {
      return item.icon(context);
    }
    return item.icon;
  };

  const getSubMenu = (item: MenuConfig<Ctx>): MenuConfig<Ctx>[] | undefined => {
    if (item === "separator") return undefined;

    if (typeof item.subMenu === "function") {
      return item.subMenu(context);
    }
    return item.subMenu;
  };

  const getIsSelected = (item: MenuConfig<Ctx>): boolean => {
    if (item === "separator") return false;

    if (typeof item.isSelected === "function") {
      return item.isSelected(context);
    }
    return item.isSelected ?? false;
  };

  return (
    <>
      <div
        ref={menuRef}
        data-context-menu
        className={cn(
          "fixed py-[6px] rounded-[6px] bg-[var(--color-card)] border border-[var(--color-outline)]",
          "scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        )}
        style={{
          zIndex: 9999,
          visibility: adjustedPosition?.isReady ? "visible" : "hidden",
          left: adjustedPosition?.x ?? position.x,
          top: adjustedPosition?.y ?? position.y,
          maxHeight: adjustedPosition?.maxHeight,
          overflowY: adjustedPosition?.maxHeight ? "auto" : undefined,
          boxShadow: isDark ? "1px 4px 8px rgba(0, 0, 0, 0.3)" : "1px 4px 8px rgba(0, 0, 0, 0.15)",
        }}
      >
        {items.map((item, index) => {
          if (item === "separator") {
            const prevItem = items
              .slice(0, index)
              .reverse()
              .find((i) => i !== "separator");
            const separatorKey = prevItem && typeof prevItem === "object" ? `sep-after-${prevItem.id}` : `sep-${index}`;
            return <div key={separatorKey} className="my-[9px] mx-[1px] border-t border-[var(--color-outline)]" />;
          }

          const disabled = isDisabled(item);
          const showNew = item.isNew && isNew(item.id, item.isNew) && !disabled && !clickedNewItems.has(item.id);
          const label = getLabel(item);
          const tooltip = getTooltip(item);
          const icon = getIcon(item);
          const hasSubmenu = (getSubMenu(item)?.length ?? 0) > 0;
          const isSelected = getIsSelected(item);

          return (
            <TooltipTrigger key={item.id} placement={adjustedPosition?.tooltipPlacement ?? "right"} block>
              <button
                type="button"
                data-item-id={item.id}
                aria-disabled={disabled}
                onClick={() => !disabled && handleItemClick(item)}
                onMouseEnter={(e) => handleSubmenuEnter(item, e)}
                onMouseLeave={handleSubmenuLeave}
                className={cn(
                  "flex w-full items-center gap-2",
                  "px-[9px] py-[4.5px] text-left transition-colors",
                  "text-[length:var(--Font-Size-Base,12px)]",
                  "bg-[var(--color-card)] text-[var(--color-foreground)]",
                  !disabled && "hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)]",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
              >
                {hasAnyIcon && (
                  <span
                    className={cn("flex items-center justify-center shrink-0 relative", showNew ? "w-auto" : "w-4 h-4")}
                  >
                    {showNew ? (
                      <div className="rounded-full flex flex-row gap-[4.5px] bg-[var(--color-primary-tonal)] items-center px-[6px] py-[1px] text-[var(--color-primary)]">
                        <div className="rounded-full w-[8px] h-[8px] bg-[var(--color-primary)]" />
                        <div>New</div>
                      </div>
                    ) : (
                      icon
                    )}
                  </span>
                )}

                <span className={cn("flex-1 mr-8 whitespace-nowrap", showNew && hasAnyIcon && "ml-1")}>{label}</span>

                {item.shortcut && !isMobile && (
                  <span className="text-[var(--color-neutral)] text-[10px]">{item.shortcut}</span>
                )}

                {isSelected && <CheckIcon className="text-[var(--color-primary)]" />}
                {hasSubmenu && <ChevronRightIcon className="ml-2" />}
              </button>
              <Tooltip>{tooltip}</Tooltip>
            </TooltipTrigger>
          );
        })}
      </div>

      {submenuState &&
        (() => {
          const activeItem = items.find((item) => item !== "separator" && item.id === submenuState.itemId);
          if (!activeItem || activeItem === "separator") return null;

          const subMenuItems = getSubMenu(activeItem);
          if (!subMenuItems || subMenuItems.length === 0) return null;

          const handleSubmenuMouseEnter = () => {
            if (closeTimeoutRef.current !== null) {
              window.clearTimeout(closeTimeoutRef.current);
              closeTimeoutRef.current = null;
            }
            if (openTimeoutRef.current !== null) {
              window.clearTimeout(openTimeoutRef.current);
              openTimeoutRef.current = null;
            }
          };

          return (
            <div onMouseEnter={handleSubmenuMouseEnter}>
              <ContextMenu
                key={submenuState.key}
                items={subMenuItems}
                context={context}
                position={submenuState.position}
                onClose={onClose}
              />
            </div>
          );
        })()}
    </>
  );
};
